import { Dayjs } from 'dayjs'
import { createSelector } from 'reselect'

import { day, getOnlyDate } from '~/astra'
import { PatientGroup } from '~/clients/derived-api-types'
import { type StoreState, useStore } from '~/store/store'
import { isMaxDuration } from '~/store/utils/blockEvaluation/ruleInstance'
import { getDateLocationIdKey } from '~/utils/dateKeys'

import { BlockSchedule, Include, selectBlockSchedule, selectBlockSchedules, useBlockSchedule, useBlockSchedules } from '../'

function buildBlockSchedulesByDateLookup(byDate: Map<string, BlockSchedule[]>) {
    return (date: Dayjs) => byDate.get(getOnlyDate(date)) ?? []
}

function buildBlockScheduleByDateAndLocationLookup(byDateLocation: Map<string, BlockSchedule>) {
    return (date: Dayjs, locationId: number) => byDateLocation.get(getDateLocationIdKey(date, locationId)) ?? null
}

/** Memoized (date) → BlockSchedule[] for createSelector chains. */
export const selectBlockSchedulesByDateLookup = createSelector(
    (state: StoreState) => selectBlockSchedules(state, { groupBy: 'scheduled_date' }),
    buildBlockSchedulesByDateLookup
)

/** Memoized (date, locationId) → BlockSchedule | null for createSelector chains. */
export const selectBlockScheduleByDateAndLocationLookup = createSelector(
    (state: StoreState) => selectBlockSchedules(state, { keyBy: ['location_id', 'scheduled_date'] }),
    buildBlockScheduleByDateAndLocationLookup
)

export const BLOCK_SCHEDULE_WITH_LOCATION_INCLUDE = { location: true } as const

export type BlockScheduleWithLocation = Include<BlockSchedule, typeof BLOCK_SCHEDULE_WITH_LOCATION_INCLUDE>

export function getPatientGroupsFromBlockSchedule(blockSchedule: BlockSchedule | null | undefined): PatientGroup[] {
    const parameters = blockSchedule?.rule_instance?.parameters

    if (!parameters?.constraints) return []

    return isMaxDuration(parameters)
        ? parameters.constraints.patient_groups.map(constraint => constraint.patient_group)
        : parameters.constraints.map(constraint => constraint.patient_group)
}

export function selectBlockScheduleByDateAndLocationId(state: StoreState, date: Dayjs, locationId: number) {
    return selectBlockSchedule(state, {
        where: {
            scheduled_date: getOnlyDate(date),
            location_id: locationId,
        },
        include: { location: true },
    })
}

export function selectBlockSchedulesByDate(state: StoreState, date: Dayjs) {
    return selectBlockSchedules(state, {
        where: { scheduled_date: getOnlyDate(date) },
    })
}

export function selectBlockSchedulesByDateRange(state: StoreState, startDate: Dayjs, endDate: Dayjs, locationId?: number) {
    return selectBlockSchedules(state, {
        where: {
            scheduled_date: { gte: getOnlyDate(startDate), lte: getOnlyDate(endDate) },
            ...(locationId !== undefined && { location_id: locationId }),
        },
    })
}

export function selectBlockScheduleForLocationDay(state: StoreState, date: Dayjs, locationId: number) {
    return (
        selectBlockSchedule(state, {
            where: {
                scheduled_date: getOnlyDate(date),
                location_id: locationId,
            },
        }) ?? null
    )
}

export function useBlockSchedulesByDate(date: Dayjs, locationId?: number) {
    return useBlockSchedules({
        where: {
            scheduled_date: getOnlyDate(date),
            ...(locationId !== undefined && { location_id: locationId }),
        },
    })
}

export function useBlockSchedulesByDateRange(startDate: Dayjs, endDate: Dayjs, locationId?: number) {
    return useBlockSchedules({
        where: {
            scheduled_date: { gte: getOnlyDate(startDate), lte: getOnlyDate(endDate) },
            ...(locationId !== undefined && { location_id: locationId }),
        },
    })
}

export function useBlockScheduleForLocationDay(date: Dayjs, locationId: number) {
    return useBlockSchedule({
        where: {
            scheduled_date: getOnlyDate(date),
            location_id: locationId,
        },
    })
}

export function useBlockScheduleForSelectedLocationDay() {
    const selectedLocationDay = useStore(state => state.surgeryScheduler.selectedLocationDay)
    return useBlockScheduleForLocationDay(day(selectedLocationDay?.date ?? 0), selectedLocationDay?.locationId ?? 0)
}

export function usePatientGroupsForSelectedLocationDay(): PatientGroup[] {
    const blockSchedule = useBlockScheduleForSelectedLocationDay()

    return getPatientGroupsFromBlockSchedule(blockSchedule)
}
